import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { assignmentListSchema, wallClockTimeSchema } from './job';
import { listQuerySchema } from './pagination';

/*
 * Recurring jobs. reference/data-model.md §5, business-rules.md §6,
 * ROADMAP.md §7.5.
 *
 * **The occurrence arithmetic lives here, and it is pure.** `occurrencesBetween`
 * below takes a rule and a window and returns calendar-day strings — no
 * database, no `new Date()` on a local clock, no time zone. That is what makes
 * "does a fortnightly Tuesday rule land on the 14th" a unit test rather than
 * something only the nightly cron can answer, and it is the same discipline as
 * `src/client/lib/calendar.ts`: `YYYY-MM-DD` strings, sums done at UTC midnight,
 * so no daylight-saving hour can appear in the middle of a day count.
 */

export const RECURRING_FREQUENCIES = ['weekly', 'biweekly', 'monthly'] as const;
export type RecurringFrequencyValue = (typeof RECURRING_FREQUENCIES)[number];

/** Display values. A raw `biweekly` must never reach a screen (P5). */
export const RECURRING_FREQUENCY_LABELS: Record<RecurringFrequencyValue, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

/**
 * How far ahead generation reaches — business-rules.md §6 says fourteen days,
 * "a setting".
 *
 * A constant until something needs to change it without a deploy, which is the
 * bar `setting` exists for. Named and exported so the worker, the service and
 * the test all mean the same fourteen.
 */
export const GENERATION_HORIZON_DAYS = 14;

const dayOfWeekSchema = z.number().int().min(0).max(6);
const dayOfMonthSchema = z.number().int().min(1).max(31);

const recurringFields = z.object({
  clientId: z.uuid(),
  /** Null falls back to the client's primary property at generation time. */
  propertyId: z.uuid().nullable().optional(),
  categoryId: z.uuid().nullable().optional(),
  serviceTypeId: z.uuid(),

  frequency: z.enum(RECURRING_FREQUENCIES),
  dayOfWeek: dayOfWeekSchema.nullable().optional(),
  dayOfMonth: dayOfMonthSchema.nullable().optional(),

  preferredTime: wallClockTimeSchema,
  estimatedDurationMinutes: z
    .number()
    .int('Give whole minutes')
    .min(15, 'A job takes at least 15 minutes')
    .max(1440, 'A job cannot run longer than a day'),

  /** The usual crew. The same list shape an assignment takes. */
  defaultEmployeeIds: assignmentListSchema.optional(),

  startDate: calendarDateSchema,
  endDate: calendarDateSchema.nullable().optional(),
});

/**
 * A rule has to be able to produce an occurrence.
 *
 * A weekly rule with no day of the week generates nothing, forever, and reads
 * as a broken cron rather than as a half-filled form. Refused here in words,
 * and again by the migration's CHECK for anything that does not come through
 * this schema.
 */
const shapeRule = {
  check: (value: {
    frequency: RecurringFrequencyValue;
    dayOfWeek?: number | null | undefined;
    dayOfMonth?: number | null | undefined;
  }): boolean =>
    value.frequency === 'monthly'
      ? value.dayOfMonth !== null && value.dayOfMonth !== undefined
      : value.dayOfWeek !== null && value.dayOfWeek !== undefined,
  message: 'Say which day it repeats on',
  path: ['dayOfWeek'] as const,
};

const endsAfterItStarts = {
  check: (value: { startDate: string; endDate?: string | null | undefined }): boolean =>
    value.endDate === null || value.endDate === undefined || value.endDate >= value.startDate,
  message: 'The end date is before the start',
  path: ['endDate'] as const,
};

export const createRecurringRuleSchema = recurringFields
  .refine(shapeRule.check, { message: shapeRule.message, path: [...shapeRule.path] })
  .refine(endsAfterItStarts.check, {
    message: endsAfterItStarts.message,
    path: [...endsAfterItStarts.path],
  });

export type CreateRecurringRuleInput = z.infer<typeof createRecurringRuleSchema>;

/**
 * Editing does not move the client or the property.
 *
 * A rule that changes address is a different standing arrangement, and jobs it
 * has already generated carry the old one — `address_snapshot` is written once
 * per job on purpose (business-rules.md §3). Making a new rule is the honest
 * action, and it leaves the history readable.
 */
export const updateRecurringRuleSchema = recurringFields
  .omit({ clientId: true, propertyId: true })
  .extend({ isActive: z.boolean().optional() })
  .refine(shapeRule.check, { message: shapeRule.message, path: [...shapeRule.path] })
  .refine(endsAfterItStarts.check, {
    message: endsAfterItStarts.message,
    path: [...endsAfterItStarts.path],
  });

export type UpdateRecurringRuleInput = z.infer<typeof updateRecurringRuleSchema>;

export const recurringRuleListQuerySchema = listQuerySchema.extend({
  clientId: z.uuid().optional(),
  activity: z.enum(['active', 'inactive', 'all']).default('active'),
});

export type RecurringRuleListQuery = z.infer<typeof recurringRuleListQuerySchema>;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export interface RecurringRuleView {
  id: string;
  clientId: string;
  clientName: string;
  propertyId: string | null;
  /** The address as it reads today. A generated job snapshots its own. */
  propertyAddress: string | null;
  categoryId: string | null;
  categoryName: string | null;
  serviceTypeId: string;
  /** "Deep Cleaning", never `deep_cleaning` — P5, P25. */
  serviceTypeName: string;

  frequency: RecurringFrequencyValue;
  /** "Every two weeks". Resolved on the server. */
  frequencyLabel: string;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  /** "Tuesdays" / "the 14th of each month" — the whole phrase, built once. */
  scheduleLabel: string;

  preferredTime: string;
  estimatedDurationMinutes: number;

  defaultEmployeeIds: string[];
  /** Names, so the list does not render a row of UUIDs. */
  defaultEmployeeNames: string[];

  startDate: string;
  endDate: string | null;
  nextGenerateDate: string;
  lastGeneratedDate: string | null;
  isActive: boolean;

  /** How many jobs this rule has produced. Zero is `0` (P1). */
  generatedJobCount: number;
}

/** What one generation run did, per task and in total. */
export interface GenerationResult {
  /** Rules looked at. */
  rulesConsidered: number;
  created: number;
  /**
   * Occurrences that already had a job — **including cancelled ones**. This
   * number being large on a second run is the idempotency working, not a fault.
   */
  skipped: number;
  /** Rules that failed, by id and message. One bad rule does not stop the run. */
  failures: { ruleId: string; message: string }[];
}

// ---------------------------------------------------------------------------
// The occurrence arithmetic — pure, and shared so it can be tested alone
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

function toUtc(day: string): number {
  const parsed = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed)) throw new RangeError(`Not a calendar day: ${day}`);
  return parsed;
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function addDays(day: string, delta: number): string {
  return fromUtc(toUtc(day) + delta * DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
function dayOfWeekOf(day: string): number {
  return new Date(toUtc(day)).getUTCDay();
}

/** How many days a month has, so the 31st of February can be clamped. */
function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

export interface OccurrenceRule {
  frequency: RecurringFrequencyValue;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  startDate: string;
  endDate: string | null;
}

/**
 * Every date this rule falls on inside `[from, to]`, inclusive.
 *
 * **Biweekly counts from the rule's own first occurrence**, not from the window
 * — otherwise "every two weeks" would mean a different fortnight depending on
 * when somebody happened to look, and two runs a day apart could disagree about
 * whether next Tuesday is on.
 *
 * **A monthly rule on the 31st lands on the last day of a short month**, not
 * nowhere. Skipping February would mean a customer on the 31st simply does not
 * get cleaned that month, which is not what anybody chose when they picked a
 * date near the end.
 */
export function occurrencesBetween(rule: OccurrenceRule, from: string, to: string): string[] {
  if (to < from) return [];

  // Never before the rule begins, never after it ends.
  const first = from < rule.startDate ? rule.startDate : from;
  const last = rule.endDate !== null && rule.endDate < to ? rule.endDate : to;
  if (last < first) return [];

  const dates: string[] = [];

  if (rule.frequency === 'monthly') {
    const wanted = rule.dayOfMonth;
    if (wanted === null) return [];

    // Walk months, not days: a month is not a fixed number of days and adding
    // 30 would drift a rule off its date within a year.
    let year = Number(first.slice(0, 4));
    let month = Number(first.slice(5, 7)) - 1;

    for (let guard = 0; guard < 400; guard += 1) {
      const day = Math.min(wanted, daysInMonth(year, month));
      const candidate = fromUtc(Date.UTC(year, month, day));

      if (candidate > last) break;
      if (candidate >= first) dates.push(candidate);

      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }

    return dates;
  }

  const wanted = rule.dayOfWeek;
  if (wanted === null) return [];

  // The rule's own first occurrence — the anchor biweekly parity counts from.
  const offset = (wanted - dayOfWeekOf(rule.startDate) + 7) % 7;
  const anchor = addDays(rule.startDate, offset);
  const step = rule.frequency === 'biweekly' ? 14 : 7;

  // Jump straight to the first occurrence in the window rather than stepping
  // from the anchor: a rule that started three years ago would otherwise walk
  // a hundred and fifty iterations to reach next week.
  const behind = Math.max(0, Math.ceil((toUtc(first) - toUtc(anchor)) / (step * DAY_MS)));
  let at = addDays(anchor, behind * step);

  while (at <= last) {
    if (at >= first) dates.push(at);
    at = addDays(at, step);
  }

  return dates;
}

/** The first occurrence strictly after `after`, or null when the rule has ended. */
export function nextOccurrenceAfter(rule: OccurrenceRule, after: string): string | null {
  // A year is enough for every frequency here; beyond it the rule has ended.
  const [next] = occurrencesBetween(rule, addDays(after, 1), addDays(after, 400));
  return next ?? null;
}

/** "Tuesdays" / "the 14th of each month" — one phrase, built on the server. */
export function scheduleLabelFor(rule: OccurrenceRule): string {
  const WEEKDAYS = [
    'Sundays',
    'Mondays',
    'Tuesdays',
    'Wednesdays',
    'Thursdays',
    'Fridays',
    'Saturdays',
  ];

  if (rule.frequency === 'monthly') {
    const day = rule.dayOfMonth ?? 1;
    return `the ${String(day)}${ordinalSuffix(day)} of each month`;
  }

  const name = WEEKDAYS[rule.dayOfWeek ?? 0] ?? '';
  return rule.frequency === 'biweekly' ? `every other ${name.slice(0, -1)}` : name;
}

function ordinalSuffix(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
}
